import React from "react";
import { UserPlus, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useClient } from "@/lib/clientContext";
import PatientSearch from "@/components/patients/PatientSearch";
import RecentClaims from "@/components/patients/RecentClaims";
import AddRosterPatient from "@/components/patients/AddRosterPatient";

// Patients V2 — an exact duplicate of the Patients page, EXCEPT every chart-open
// goes to the new API-backed chart (ChartV2, opened as an embedded tab by
// PatientsWorkspaceV2). Search, Recent Claims (incl. "Open claim"), ⌘K and row
// actions are otherwise identical. The original Patients page is untouched.
export default function PatientsV2({ onOpenChart, onOpenClaim } = {}) {
  const { selectedClientId } = useClient();

  // Notes carry no chart id — resolve the patient's chart on demand from the
  // roster (by MRN, then name) scoped to the note's own client. Returns a uuid or null.
  const resolveChartUuid = async (patient) => {
    if (patient.chart_uuid || patient.chart_id) return patient.chart_uuid || patient.chart_id;
    const cid = patient.client_id || selectedClientId;
    const q = patient.mrn || patient.full_name || "";
    if (!cid || !q) return null;
    try {
      const { data } = await api.get("/api/edi/note-to-claim/pg-patient-search", { params: { client_id: cid, q } });
      const results = Array.isArray(data?.results) ? data.results : [];
      const nm = (patient.full_name || "").toLowerCase();
      const match =
        (patient.mrn && results.find((r) => String(r.mrn) === String(patient.mrn))) ||
        results.find((r) => (r.full_name || `${r.first_name || ""} ${r.last_name || ""}`).trim().toLowerCase() === nm) ||
        results[0];
      return match?.chart_uuid || match?.chart_id || null;
    } catch { return null; }
  };

  const openChart = async (patient, tab) => {
    // Embedded path opens a ChartV2 tab that shows its own "Loading patient chart…"
    // cover, so a loading toast here is redundant (and a create-then-dismiss in the
    // same tick can leave the toast stuck). Only toast the standalone/error cases.
    const hasHost = typeof onOpenChart === "function";
    const t = hasHost ? null : toast.loading("Opening chart…");
    const uuid = await resolveChartUuid(patient);
    if (t) toast.dismiss(t);
    if (!uuid) { toast.error("No linked chart found for this patient."); return; }
    const enriched = { ...patient, chart_uuid: uuid };
    if (hasHost) { onOpenChart(enriched, tab); return; }
    const qs = new URLSearchParams({ uuid });
    if (tab && tab !== "focus") qs.set("tab", tab);
    window.open(`/chart-v2?${qs.toString()}`, "_blank");
  };

  const onFinance = (patient) => openChart(patient, "finance");
  const onSchedule = (patient) => openChart(patient, "engagement");
  // V2 dictation lives inside the chart's encounter panel — open the chart tab
  // (the user clicks "Start encounter" there) instead of the standalone /dictate page.
  const onDictate = (patient) => openChart(patient, "focus");

  const Landing = (
    <div className="pb-12" data-testid="patients-v2-landing">
      <RecentClaims
        v2
        onOpenChart={openChart}
        onOpenClaim={onOpenClaim}
        onDictate={onDictate}
        onFinance={onFinance}
        onSchedule={onSchedule}
        resolveChart={resolveChartUuid}
      />
    </div>
  );

  return (
    <div className="max-w-[1160px] mx-auto" data-testid="patients-v2-view">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-[var(--ink)] flex items-center gap-2">
            <UserPlus size={20} className="text-[var(--gray2)]" /> Patients
          </h1>
          <p className="text-[13px] text-[var(--gray2)] mt-0.5">Search a patient to open their chart, or pick up a claim that needs review.</p>
        </div>
        <AddRosterPatient clientId={selectedClientId} onAdded={() => {}} />
      </div>

      {!selectedClientId ? (
        <div className="mt-10 rounded-2xl border border-dashed border-black/15 bg-black/[.015] py-16 text-center" data-testid="patients-v2-no-client">
          <Search size={28} className="mx-auto text-[var(--gray3)]" />
          <div className="text-[15px] font-semibold mt-3">Select a Client ID to search patients</div>
          <div className="text-[13px] text-[var(--gray2)] mt-1">Use the client dropdown in the top bar (left of “+ Upload”) to choose a client.</div>
        </div>
      ) : (
        <PatientSearch
          clientId={selectedClientId}
          onOpenChart={openChart}
          onDictate={onDictate}
          onFinance={onFinance}
          onSchedule={onSchedule}
        >
          {Landing}
        </PatientSearch>
      )}
    </div>
  );
}
